import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Terms & Conditions</h1>
        <p className="text-muted-foreground mb-8">Last Updated: November 10, 2025</p>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Acceptance of Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                By accessing and using Vocalizing ("the Platform"), you accept and agree to be bound by these Terms and Conditions. 
                If you do not agree to these terms, please do not use our services.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. User Accounts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>2.1 Registration:</strong> You must create an account to use our services. You agree to provide accurate, current, and complete information during registration.</p>
              <p><strong>2.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>
              <p><strong>2.3 Account Types:</strong> We offer different account types (Student, Coach, Admin) with varying levels of access and permissions.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Booking and Payment Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>3.1 Pricing:</strong> Lessons are priced at $1.99 per minute. The total cost is calculated based on your selected lesson duration (15-120 minutes).</p>
              <p><strong>3.2 Payment Processing:</strong> All payments are processed securely through Stripe. Your payment method will be authorized at the time of booking.</p>
              <p><strong>3.3 Payment Hold:</strong> Payment is held (not charged) when you book a lesson. The charge is only processed after your coach accepts the booking.</p>
              <p><strong>3.4 Cancellation:</strong> If a coach denies your booking request, your payment authorization will be canceled, and you will not be charged.</p>
              <p><strong>3.5 Refunds:</strong> Refunds are processed according to our cancellation policy. Once a lesson is confirmed and completed, refunds are subject to review.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Coach Requirements and Responsibilities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>4.1 Application:</strong> Coaches must apply and be approved by our admin team before offering services on the Platform.</p>
              <p><strong>4.2 Qualifications:</strong> Coaches represent that they have the necessary qualifications, experience, and skills to provide vocal coaching services.</p>
              <p><strong>4.3 Availability:</strong> Coaches are responsible for maintaining accurate availability and managing their booking calendar.</p>
              <p><strong>4.4 Lesson Delivery:</strong> Coaches must conduct lessons professionally, punctually, and through the Platform's integrated video conferencing.</p>
              <p><strong>4.5 Payment:</strong> Coaches receive payment for accepted and completed lessons, minus platform fees, according to our payment schedule.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Student Responsibilities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>5.1 Lesson Attendance:</strong> Students are expected to attend scheduled lessons at the agreed-upon time.</p>
              <p><strong>5.2 Equipment:</strong> Students are responsible for having appropriate equipment (computer/device, internet connection, microphone) for online lessons.</p>
              <p><strong>5.3 Conduct:</strong> Students must treat coaches with respect and professionalism during all interactions.</p>
              <p><strong>5.4 Cancellation Notice:</strong> Students should provide reasonable notice if they need to cancel or reschedule a confirmed lesson.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Platform Usage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>6.1 Prohibited Activities:</strong> You may not use the Platform to engage in any illegal, fraudulent, or harmful activities.</p>
              <p><strong>6.2 Content:</strong> You are responsible for any content you upload or share through the Platform.</p>
              <p><strong>6.3 Video Conferencing:</strong> All lessons must be conducted through our integrated video conferencing system.</p>
              <p><strong>6.4 Communication:</strong> Users agree to receive transactional emails related to bookings, schedules, and account management.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>7.1 Platform Ownership:</strong> All rights, title, and interest in the Platform, including design, functionality, and content, belong to Vocalizing.</p>
              <p><strong>7.2 User Content:</strong> You retain ownership of content you create, but grant us a license to use it for operating the Platform.</p>
              <p><strong>7.3 Lesson Materials:</strong> Coaches retain ownership of their teaching materials. Students may not record, reproduce, or distribute lesson content without express permission.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Privacy and Data Protection</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Your use of the Platform is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>9.1 Service Availability:</strong> We strive to maintain uninterrupted service but do not guarantee the Platform will be available at all times.</p>
              <p><strong>9.2 Third-Party Services:</strong> We use third-party services (Stripe, video conferencing) and are not liable for their failures or issues.</p>
              <p><strong>9.3 Educational Results:</strong> We do not guarantee specific learning outcomes or vocal improvements as results vary by individual.</p>
              <p><strong>9.4 Disputes:</strong> Disputes between students and coaches should be resolved directly. We may mediate but are not responsible for the outcome.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Termination</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>10.1 User Termination:</strong> You may terminate your account at any time by contacting us.</p>
              <p><strong>10.2 Platform Termination:</strong> We reserve the right to suspend or terminate accounts that violate these Terms or engage in inappropriate behavior.</p>
              <p><strong>10.3 Effect of Termination:</strong> Upon termination, your access to the Platform will cease, and pending bookings may be canceled.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>11. Modifications to Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                We may update these Terms and Conditions from time to time. We will notify users of significant changes. 
                Continued use of the Platform after changes constitutes acceptance of the new terms.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>12. Governing Law</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                These Terms and Conditions are governed by and construed in accordance with applicable laws. 
                Any disputes arising from these terms will be resolved in the appropriate jurisdiction.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>13. Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                For questions about these Terms and Conditions, please contact us through the Platform's support system 
                or email us at support@vocalizing.com.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Terms;
